// hooks/useTrialStatus.js - COMPLETE FIXED VERSION
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

export const useTrialStatus = () => {
  const { currentUser, hasActiveSubscription } = useAuth();
  const [trialStatus, setTrialStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [showExpirationModal, setShowExpirationModal] = useState(false);

  const fetchTrialStatus = useCallback(async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      
      // FIRST: Check if user has active paid subscription
      try {
        const subscriptionResponse = await fetch('/api/subscription/status', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (subscriptionResponse.ok) {
          const subscriptionData = await subscriptionResponse.json();
          
          // If user has active paid subscription, override trial status completely
          if (subscriptionData.is_active && subscriptionData.subscription && 
              !subscriptionData.subscription.is_trial) {
            console.log('User has active paid subscription - overriding trial status');
            setTrialStatus({
              trial_state: 'not_applicable',
              status: 'paid_subscription_active',
              has_active_subscription: true,
              time_remaining_seconds: 0,
              overridden: true
            });
            setTimeRemaining(0);
            setShowExpirationModal(false);
            setLoading(false);
            return;
          }
        }
      } catch (subError) {
        console.warn('Could not check subscription status:', subError);
        // Continue with trial status check if subscription check fails
      }

      // SECOND: Only check trial status if no active paid subscription found
      const response = await fetch('/api/user/trial-status', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch trial status');
      }

      const data = await response.json();
      
      // DOUBLE-CHECK: Even if trial status says expired, verify subscription status
      if (data.trial_state === 'expired') {
        try {
          const doubleCheckResponse = await fetch('/api/subscription/status', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (doubleCheckResponse.ok) {
            const doubleCheckData = await doubleCheckResponse.json();
            if (doubleCheckData.is_active && doubleCheckData.subscription && 
                !doubleCheckData.subscription.is_trial) {
              // User actually has active paid subscription despite expired trial
              console.log('Double-check confirmed active paid subscription');
              setTrialStatus({
                trial_state: 'not_applicable',
                status: 'paid_subscription_active',
                has_active_subscription: true,
                time_remaining_seconds: 0,
                overridden: true
              });
              setTimeRemaining(0);
              setShowExpirationModal(false);
              setLoading(false);
              return;
            }
          }
        } catch (checkError) {
          console.warn('Double-check failed:', checkError);
        }
      }
      
      // If we get here, use the actual trial status
      setTrialStatus(data);
      
      // Calculate time remaining only for actual trials
      if (data.time_remaining_seconds && data.status === 'trial') {
        setTimeRemaining(data.time_remaining_seconds);
      } else {
        setTimeRemaining(0);
      }

      // Show modal only for actual trials that are expiring/expired
      if (data.status === 'trial' && 
          (data.trial_state === 'expiring_soon' || data.trial_state === 'expired')) {
        setShowExpirationModal(true);
      } else {
        setShowExpirationModal(false);
      }

    } catch (err) {
      setError(err.message);
      console.error('Error fetching trial status:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser, hasActiveSubscription]);

  // Update countdown timer only for actual trials
  useEffect(() => {
    if (!timeRemaining || timeRemaining <= 0 || 
        !trialStatus || trialStatus.status !== 'trial') return;

    const interval = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          fetchTrialStatus();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [timeRemaining, fetchTrialStatus, trialStatus]);

  // Initial fetch and periodic refresh
  useEffect(() => {
    fetchTrialStatus();
    
    // Refresh trial status every 5 minutes
    const interval = setInterval(fetchTrialStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchTrialStatus]);

  const formatTimeRemaining = useCallback((seconds) => {
    if (!seconds || seconds <= 0 || 
        !trialStatus || trialStatus.status !== 'trial') return '';
    
    const days = Math.floor(seconds / (24 * 60 * 60));
    const hours = Math.floor((seconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((seconds % (60 * 60)) / 60);
    
    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} ${hours}h remaining`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    } else {
      return `${minutes} minute${minutes > 1 ? 's' : ''} remaining`;
    }
  }, [trialStatus]);

  // CRITICAL FIX: If user has active paid subscription, trial status is irrelevant
  // Also check if trialStatus was overridden by paid subscription
  const isTrialExpired = (hasActiveSubscription || 
                         (trialStatus && trialStatus.overridden)) ? 
                         false : trialStatus?.trial_state === 'expired';
  
  const isTrialExpiringSoon = (hasActiveSubscription || 
                              (trialStatus && trialStatus.overridden)) ? 
                              false : trialStatus?.trial_state === 'expiring_soon';
  
  const isTrialActive = (hasActiveSubscription || 
                        (trialStatus && trialStatus.overridden)) ? 
                        false : (trialStatus?.status === 'trial' && !isTrialExpired);

  return {
    trialStatus,
    loading,
    error,
    timeRemaining,
    formattedTimeRemaining: formatTimeRemaining(timeRemaining),
    isTrialExpired,
    isTrialExpiringSoon,
    isTrialActive,
    showExpirationModal,
    setShowExpirationModal,
    refreshTrialStatus: fetchTrialStatus
  };
};